/**
 * UnmatchedTransactionsFeed
 * Shows bank transactions the system could not match automatically.
 * Each row has a "Match to Lease" button and a "Dismiss ▾" dropdown with
 * one-time dismiss and "Ignore all like this" (smart rule builder) options.
 */

import { useState, useEffect, useRef } from 'react';
import { Link2, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import {
  manualMatchTransaction,
  fetchActiveLeases,
  type UnmatchedTransaction,
  type ActiveLeaseSummary,
} from '../../api/external-payments-hub.api';
import { SmartRuleBuilder } from './SmartRuleBuilder';
import { formatLocalDate } from '../../utils/date';
import { formatUnitLabel } from '@resida/shared';

interface UnmatchedTransactionsFeedProps {
  transactions: UnmatchedTransaction[];
  totalCount: number;
  onMatched: () => Promise<void> | void;
  onDismiss: (transactionId: string) => Promise<void>;
  onIgnore: (
    transactionId: string,
    rule: { field: string; operator: string; value: string }
  ) => Promise<void>;
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Math.abs(amount));

// ---------------------------------------------------------------------------
// Lease Picker Popover
// ---------------------------------------------------------------------------

interface LeasePickerProps {
  transactionId: string;
  onSelect: (leaseId: string, tenantId: string) => Promise<void>;
  onCancel: () => void;
}

function LeasePicker({ transactionId, onSelect, onCancel }: LeasePickerProps) {
  const [leases, setLeases] = useState<ActiveLeaseSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedLeaseId, setSelectedLeaseId] = useState<string>('');
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    fetchActiveLeases()
      .then((data) => setLeases(data))
      .catch((err) => setLoadError(err instanceof Error ? err.message : 'Failed to load leases'))
      .finally(() => setIsLoading(false));
  }, []);

  const handleLeaseChange = (leaseId: string) => {
    setSelectedLeaseId(leaseId);
    setSelectedTenantId('');
    setSubmitError(null);
  };

  const selectedLease = leases.find((l) => l.id === selectedLeaseId);

  const handleSubmit = async () => {
    if (!selectedLeaseId || !selectedTenantId) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await onSelect(selectedLeaseId, selectedTenantId);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to match transaction');
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="mt-2 rounded-lg border border-border bg-muted/30 p-3"
      data-testid={`lease-picker-${transactionId}`}
    >
      <p className="text-xs font-medium text-foreground mb-2">Select a lease to match</p>

      {isLoading && (
        <p className="text-xs text-muted-foreground">Loading leases...</p>
      )}

      {loadError && (
        <p className="text-xs text-destructive">{loadError}</p>
      )}

      {!isLoading && !loadError && (
        <>
          <div className="flex flex-col gap-2">
            <select
              className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-base md:text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              value={selectedLeaseId}
              onChange={(e) => handleLeaseChange(e.target.value)}
              data-testid={`lease-picker-lease-select-${transactionId}`}
              aria-label="Select lease"
            >
              <option value="">— Select a lease —</option>
              {leases.map((lease) => (
                <option key={lease.id} value={lease.id}>
                  {lease.propertyName}{formatUnitLabel(lease.unitNumber) ? ` · ${formatUnitLabel(lease.unitNumber)}` : ''} · {formatCurrency(lease.rentAmount)}/mo
                </option>
              ))}
            </select>

            {selectedLease && selectedLease.tenants.length > 0 && (
              <select
                className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-base md:text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                value={selectedTenantId}
                onChange={(e) => setSelectedTenantId(e.target.value)}
                data-testid={`lease-picker-tenant-select-${transactionId}`}
                aria-label="Select tenant"
              >
                <option value="">— Select a tenant —</option>
                {selectedLease.tenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.firstName} {tenant.lastName}
                  </option>
                ))}
              </select>
            )}
          </div>

          {submitError && (
            <p className="text-xs text-destructive mt-1.5">{submitError}</p>
          )}

          <div className="flex items-center gap-2 mt-2.5">
            <Button
              size="sm"
              className="h-7 px-3 text-xs"
              onClick={handleSubmit}
              disabled={!selectedLeaseId || !selectedTenantId || isSubmitting}
              data-testid={`lease-picker-confirm-${transactionId}`}
            >
              {isSubmitting ? 'Matching...' : 'Confirm Match'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-3 text-xs"
              onClick={onCancel}
              disabled={isSubmitting}
              data-testid={`lease-picker-cancel-${transactionId}`}
            >
              Cancel
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Transaction Row
// ---------------------------------------------------------------------------

interface TransactionRowProps {
  transaction: UnmatchedTransaction;
  onMatched: () => Promise<void> | void;
  onDismiss: (transactionId: string) => Promise<void>;
  onIgnore: (
    transactionId: string,
    rule: { field: string; operator: string; value: string }
  ) => Promise<void>;
}

function TransactionRow({ transaction, onMatched, onDismiss, onIgnore }: TransactionRowProps) {
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isMatching, setIsMatching] = useState(false);
  const [isDismissDropdownOpen, setIsDismissDropdownOpen] = useState(false);
  const [isRuleBuilderOpen, setIsRuleBuilderOpen] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);
  const [isIgnoring, setIsIgnoring] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [ignoreSubmitError, setIgnoreSubmitError] = useState<string | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!isDismissDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDismissDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isDismissDropdownOpen]);

  const handleSelect = async (leaseId: string, tenantId: string) => {
    setIsMatching(true);
    await manualMatchTransaction(transaction.transactionId, leaseId, tenantId);
    setIsPickerOpen(false);
    setIsMatching(false);
    await onMatched();
  };

  const handleDismiss = async () => {
    setIsDismissDropdownOpen(false);
    setIsDismissing(true);
    setActionError(null);
    try {
      await onDismiss(transaction.transactionId);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to dismiss transaction');
      setIsDismissing(false);
    }
  };

  const handleIgnoreSubmit = async (rule: {
    field: string;
    operator: string;
    value: string;
  }) => {
    setIsIgnoring(true);
    setIgnoreSubmitError(null);
    try {
      await onIgnore(transaction.transactionId, rule);
      setIsRuleBuilderOpen(false);
    } catch (err) {
      setIgnoreSubmitError(err instanceof Error ? err.message : 'Failed to create ignore rule');
    } finally {
      setIsIgnoring(false);
    }
  };

  const isActionPending = isMatching || isDismissing || isIgnoring;

  return (
    <>
      <div
        className="rounded-lg border border-border bg-card p-3"
        data-testid={`unmatched-transaction-${transaction.transactionId}`}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-foreground">
                {formatCurrency(transaction.amount)}
              </span>
              <span className="text-xs text-muted-foreground">
                {formatLocalDate(transaction.date, { month: 'short', day: 'numeric' })}
              </span>
            </div>
            <p
              className="text-xs text-muted-foreground mt-0.5 truncate"
              title={transaction.description}
            >
              {transaction.description}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {transaction.institutionName} ···{transaction.accountMask}
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-1.5 shrink-0">
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2.5 text-xs"
              onClick={() => {
                setIsPickerOpen((v) => !v);
                setIsDismissDropdownOpen(false);
              }}
              disabled={isActionPending}
              data-testid={`match-to-lease-btn-${transaction.transactionId}`}
              aria-label="Match transaction to lease"
            >
              <Link2 className="h-3 w-3 mr-1" />
              Match to Lease
            </Button>

            {/* Dismiss split button */}
            <div className="relative" ref={dropdownRef}>
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2 text-xs"
                onClick={() => {
                  setIsDismissDropdownOpen((v) => !v);
                  setIsPickerOpen(false);
                }}
                disabled={isActionPending}
                data-testid={`dismiss-dropdown-btn-${transaction.transactionId}`}
                aria-label="Dismiss options"
                aria-haspopup="true"
                aria-expanded={isDismissDropdownOpen}
              >
                {isDismissing ? (
                  <span>...</span>
                ) : (
                  <>
                    Dismiss
                    <ChevronDown className="h-3 w-3 ml-1" />
                  </>
                )}
              </Button>

              {isDismissDropdownOpen && (
                <div className="absolute right-0 top-full mt-1 z-20 min-w-[180px] rounded-lg border border-border bg-background shadow-md py-1">
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-muted/50 transition-colors"
                    onClick={handleDismiss}
                    data-testid={`dismiss-one-time-btn-${transaction.transactionId}`}
                  >
                    Dismiss
                  </button>
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-muted/50 transition-colors"
                    onClick={() => {
                      setIsDismissDropdownOpen(false);
                      setIsRuleBuilderOpen(true);
                    }}
                    data-testid={`ignore-all-like-this-btn-${transaction.transactionId}`}
                  >
                    Ignore all like this
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {actionError && (
          <p className="text-xs text-destructive mt-1.5" role="alert">
            {actionError}
          </p>
        )}

        {isPickerOpen && (
          <LeasePicker
            transactionId={transaction.transactionId}
            onSelect={handleSelect}
            onCancel={() => setIsPickerOpen(false)}
          />
        )}
      </div>

      {isRuleBuilderOpen && (
        <SmartRuleBuilder
          transaction={transaction}
          onSubmit={handleIgnoreSubmit}
          onCancel={() => {
            setIsRuleBuilderOpen(false);
            setIgnoreSubmitError(null);
          }}
          isSubmitting={isIgnoring}
          submitError={ignoreSubmitError}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Feed
// ---------------------------------------------------------------------------

export function UnmatchedTransactionsFeed({
  transactions,
  totalCount,
  onMatched,
  onDismiss,
  onIgnore,
}: UnmatchedTransactionsFeedProps) {
  if (transactions.length === 0) {
    return (
      <div
        className="flex flex-col items-center justify-center py-8 text-center"
        data-testid="unmatched-transactions-empty-state"
      >
        <p className="text-sm text-muted-foreground">No unmatched transactions</p>
        <p className="text-xs text-muted-foreground mt-1">
          Deposits that cannot be matched automatically will appear here
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2" data-testid="unmatched-transactions-feed">
      {transactions.map((tx) => (
        <TransactionRow
          key={tx.transactionId}
          transaction={tx}
          onMatched={onMatched}
          onDismiss={onDismiss}
          onIgnore={onIgnore}
        />
      ))}

      {totalCount > transactions.length && (
        <p className="text-xs text-center text-muted-foreground py-2">
          Showing {transactions.length} of {totalCount} unmatched transactions
        </p>
      )}
    </div>
  );
}
